// page-actions/create-action.ts — Création d'une action depuis #actions :
// CTA « Ajouter » (modale sur desktop, saisie rapide inline sur mobile) et
// duplication en page.
//
// Huitième tranche extraite de `renderActionsPage` (point 16, 2026-09-08).
// La page ne garde que l'insertion de l'action créée dans sa liste.
//
// Globaux ambiants : api, t, toast, currentUserId, getDefaultStatusId,
// safeWarn, router, window.showEditActionModal (core-globals / window.d.ts).

import {
  buildCreateActionPayload,
  type EditorSavedAction,
} from '../../domain/action-create-payload.js';
import { openActionEditor } from './actions-table.js';
import { isMobile } from './state.js';
import {
  mountCaptureBar,
  type CaptureUser,
  type CaptureProject,
  type CaptureCommittee,
  type CaptureStructure,
} from '../component-action-capture/index.js';

type IdList = Array<number | { id: number }> | string | undefined;

/** Champs d'une action lus par la duplication. */
export interface DuplicableAction {
  text?: string;
  deadline?: string | null;
  deadline_revised?: string | null;
  status_id?: number | null;
  progress?: number;
  priority?: number | null;
  approver_id?: number | null;
  performers?: IdList;
  consulted?: IdList;
  informed?: IdList;
  project_ids?: number[];
  committee_ids?: number[];
}

function toIds(v: IdList): number[] {
  if (!v) return [];
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as number[];
    } catch {
      return [];
    }
  }
  return v.map((x) => (typeof x === 'number' ? x : x.id));
}

/**
 * Copie d'une action pour l'éditeur en mode « nouvelle action ». Les origines
 * ne sont volontairement pas répliquées : la copie est une action neuve.
 */
export function duplicatePrefill(source: DuplicableAction): Record<string, unknown> {
  return {
    text: source.text,
    deadline: source.deadline,
    deadline_revised: source.deadline_revised,
    status_id: source.status_id,
    progress: source.progress,
    priority: source.priority,
    approver_id: source.approver_id,
    performers: toIds(source.performers),
    consulted: toIds(source.consulted),
    informed: toIds(source.informed),
    project_ids: [...(source.project_ids || [])],
    committee_ids: [...(source.committee_ids || [])],
    origins: [],
  };
}

/**
 * Dupliquer en PAGE (pas en modale) : pose la copie en sessionStorage puis
 * ouvre #action/0, où page-action.ts héberge l'éditeur pré-rempli. Le hash
 * et la sélection courants sont mémorisés pour le retour.
 */
export function duplicateActionAsPage(
  source: DuplicableAction,
  selectedIds: ReadonlySet<number>,
): void {
  try {
    sessionStorage.setItem(
      'actionEditor:duplicatePrefill',
      JSON.stringify(duplicatePrefill(source)),
    );
  } catch {
    /* sessionStorage indisponible : la page s'ouvrira vierge */
  }
  // Mémorisation du hash de retour et de la sélection : même contrat que
  // l'ouverture d'une action existante.
  openActionEditor(0, selectedIds);
}

/** Référentiels transmis à l'éditeur et à la saisie rapide. */
export interface CreateActionRefs {
  users: unknown[];
  statuses: unknown[];
  projects: unknown[];
  committees: unknown[];
  structures: Array<{ is_active?: boolean }>;
}

/** Gabarit d'une action neuve : priorité normale, approbateur = moi. */
export function newActionTemplate(): Record<string, unknown> {
  return {
    text: '',
    priority: 2,
    approver_id: currentUserId(),
    deadline: null,
    deadline_revised: null,
    status_id: getDefaultStatusId(),
    progress: 0,
    performers: [],
    consulted: [],
    informed: [],
    origins: [],
    origin: '',
    project_ids: [],
  };
}

/**
 * Crée l'action depuis les données de l'éditeur, puis ajoute en série les
 * notes saisies. Un échec de note est silencieux (l'action existe déjà, on
 * ne rouvre pas l'éditeur pour ça). Renvoie l'action créée, ou `null` après
 * un toast si la création échoue.
 */
export async function createActionFromEditor<A extends { id?: number }>(
  data: EditorSavedAction,
  notes: unknown,
  notify = true,
): Promise<A | null> {
  try {
    const created = (await api.createAction(buildCreateActionPayload(data), { notify })) as A;
    const pending = (notes as string[] | undefined) || [];
    if (created.id != null && pending.length > 0) {
      for (const n of pending) {
        if (!n) continue;
        try {
          await api.addActionNote(created.id, { comment: n, user_id: currentUserId() });
        } catch (e) {
          safeWarn('addActionNote on create', e);
        }
      }
    }
    toast(t('pageActions.toast.created'));
    return created;
  } catch (err: unknown) {
    toast(err instanceof Error ? err.message : t('common.error'), 'error');
    return null;
  }
}

/**
 * Saisie rapide inline (mobile) dans `#act-mobile-capture` : montée à la
 * première ouverture puis simplement affichée ou masquée. Réutilise la barre
 * de capture du header en mode non compact.
 */
export function createMobileCapture(refs: CreateActionRefs): () => void {
  let mounted = false;
  return () => {
    const host = document.getElementById('act-mobile-capture');
    if (!host) return;
    if (!mounted) {
      mounted = true;
      mountCaptureBar(host, {
        users: refs.users as CaptureUser[],
        projects: refs.projects as CaptureProject[],
        committees: refs.committees as CaptureCommittee[],
        structures: refs.structures.filter((s) => s.is_active !== false) as CaptureStructure[],
        defaultStatusId: getDefaultStatusId(),
        onCreated: () => router.dispatch(),
        compact: false,
      });
    }
    const isOpen = host.style.display !== 'none';
    host.style.display = isOpen ? 'none' : 'block';
    if (!isOpen) {
      host.querySelector<HTMLInputElement>('.acap-input')?.focus();
      host.scrollIntoView({ block: 'nearest' });
    }
  };
}

/**
 * Câble le CTA « Ajouter » du header. Mobile : déploie la saisie rapide (la
 * modale est trop large sur petit écran). Desktop : modale d'édition, puis
 * `onCreated` avec l'action créée.
 */
export function wireNewActionButton<A extends { id?: number }>(
  refs: CreateActionRefs,
  onCreated: (created: A) => void,
): void {
  const toggleMobileCapture = createMobileCapture(refs);
  document.getElementById('btn-new-action')?.addEventListener('click', () => {
    if (isMobile()) {
      toggleMobileCapture();
      return;
    }
    window.showEditActionModal(
      newActionTemplate() as never,
      {
        users: refs.users,
        statuses: refs.statuses,
        projects: refs.projects,
        onSaved: async (data: EditorSavedAction, notes: unknown, notify = true) => {
          const created = await createActionFromEditor<A>(data, notes, notify);
          if (created) onCreated(created);
        },
      } as never,
    );
  });
}
